import { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import { Dialog } from "@lopie/kernel-ui";

/**
 * Replacing the code an installed app runs with a copy of it, and putting it back.
 *
 * ## What this is for
 *
 * An app that is installed and in use has data, and a copy of it built in
 * Builder starts with none. Somebody who is missing one feature in the vault
 * they already use does not want a second, empty vault: they want the one they
 * have to do the thing. So an instance can be *adopted* by a copy of its app.
 * It keeps its name, its address, its tables and its secrets, and is built from
 * the copy's code from then on.
 *
 * ## What the screen owes the person
 *
 * The decision is made on the plan, not on the button. The kernel compares the
 * two apps' own tables, keys and secrets and says what would be the same, what
 * is new, and what would stop it; every block is shown as the reason it cannot
 * go ahead, in words, with the part of the app it is about. A refusal is
 * information and an empty list of problems is a different sentence from a list
 * that could not be built — the second says so.
 *
 * What the plan cannot say is whether the copy's *code* understands the records
 * already there. That is said next to the button that does the thing, and the
 * checkbox is the person agreeing to it with the restore point in view: this
 * is the one caveat on the screen that is a dialog's, because it is the one
 * nobody sees twice by accident.
 *
 * Planning, doing and undoing are a governor's. This screen does not decide who
 * that is: the kernel answers `not_a_governor` and the dialog says so.
 */

const STATE_WORDS = {
  kept: "kept",
  added: "new, starts empty",
  removed: "dropped by the copy",
  key_changed: "key changed",
};

/** The copies of `app` the Library offers, and each one's releases. A copy of a
 *  copy counts: it was made from the same code. */
export function copiesOf(catalog, app) {
  const byApp = Object.fromEntries((catalog || []).map((e) => [e.app, e]));
  const descends = (entry) => {
    const seen = new Set();
    let cursor = entry;
    for (let i = 0; i < 10 && cursor; i += 1) {
      const parent = cursor.derivedFrom?.app;
      if (!parent || seen.has(parent)) return false;
      if (parent === app) return true;
      seen.add(parent);
      cursor = byApp[parent];
    }
    return false;
  };
  return (catalog || [])
    .filter((e) => e.app !== app && descends(e) && (e.packageVersions || []).length)
    .map((e) => ({
      app: e.app,
      name: e.name || e.app,
      versions: e.packageVersions,
      defaultVersion: e.defaultVersion || e.packageVersions[e.packageVersions.length - 1],
      description: e.description || "",
    }));
}

function words(error) {
  const body = error?.body || {};
  return body.detail || error?.message || String(error);
}

function Problems({ title, severity, rows }) {
  if (!rows?.length) return null;
  return (
    <Alert severity={severity} variant="outlined">
      <AlertTitle>{title}</AlertTitle>
      <Stack spacing={0.75} component="ul" sx={{ m: 0, pl: 2 }}>
        {rows.map((r) => (
          <li key={`${r.code}:${r.subject}`}>
            {r.subject ? <strong>{r.subject}: </strong> : null}
            {r.detail}
          </li>
        ))}
      </Stack>
    </Alert>
  );
}

function Parts({ label, rows }) {
  if (!rows?.length) return null;
  return (
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>{label}</Typography>
      <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", gap: 1 }}>
        {rows.map((r) => (
          <Chip key={r.name} size="small" variant="outlined"
            label={`${r.name} · ${STATE_WORDS[r.state] || r.state}`}
            color={r.state === "kept" || r.state === "added" ? "default" : "warning"} />
        ))}
      </Stack>
    </Box>
  );
}

function Ceiling({ ceiling }) {
  if (!ceiling) return null;
  const lines = [];
  const grant = (g) => `${g.type}${g.tags?.length ? ` (${g.tags.join(", ")})` : ""}`;
  for (const [key, label] of [["reads", "read"], ["service_reads", "read with nobody signed in"]]) {
    for (const g of ceiling[key]?.added || []) lines.push(`May also ${label} ${grant(g)}.`);
    for (const g of ceiling[key]?.removed || []) lines.push(`May no longer ${label} ${grant(g)}.`);
  }
  for (const [key, label] of [["actions", "do"], ["service_actions", "do with nobody signed in"]]) {
    for (const a of ceiling[key]?.added || []) lines.push(`May also ${label} ${a}.`);
    for (const a of ceiling[key]?.removed || []) lines.push(`May no longer ${label} ${a}.`);
  }
  return (
    <Box>
      <Typography variant="overline" color="text.secondary" sx={{ display: "block" }}>
        What it may touch afterwards
      </Typography>
      {lines.length ? (
        <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2 }}>
          {lines.map((l) => <li key={l}><Typography variant="body2">{l}</Typography></li>)}
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          The same as it may today: the copy was approved for the same reads and actions.
        </Typography>
      )}
    </Box>
  );
}

/** Choose a copy, read what the change would do, and agree to it. */
export function AdoptDialog({ open, instance, copies, busy, onClose, onPlan, onAdopt }) {
  const [app, setApp] = useState("");
  const [version, setVersion] = useState("");
  const [plan, setPlan] = useState(null);
  const [planning, setPlanning] = useState(false);
  const [ack, setAck] = useState(false);
  const [failure, setFailure] = useState(null);

  const chosen = useMemo(() => (copies || []).find((c) => c.app === app) || null, [copies, app]);

  useEffect(() => {
    if (!open) return;
    const first = (copies || [])[0];
    setApp(first?.app || "");
    setVersion(first?.defaultVersion || "");
    setPlan(null);
    setAck(false);
    setFailure(null);
  }, [open, instance?.name]); // eslint-disable-line react-hooks/exhaustive-deps

  const name = instance?.displayName || instance?.name || "";
  const forget = () => { setPlan(null); setAck(false); setFailure(null); };

  const check = async () => {
    setPlanning(true);
    forget();
    try {
      setPlan(await onPlan(instance.name, app, version));
    } catch (e) {
      setFailure(words(e));
    } finally {
      setPlanning(false);
    }
  };

  const submit = async () => {
    setFailure(null);
    try {
      await onAdopt(instance.name, app, version);
    } catch (e) {
      // A 409 carries the plan it was refused on, which is the thing to show.
      if (e?.body?.blocks) setPlan(e.body);
      setFailure(words(e));
    }
  };

  const ready = Boolean(plan?.can_adopt) && ack && !busy;

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="md">
      <DialogTitle>Replace what {name} runs with a copy</DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <Typography variant="body2" color="text.secondary">
            {name} keeps its name, its address, its tables and its secrets, and is
            rebuilt from the copy&apos;s code. Nothing is copied or moved: the copy
            opens the tables {name} already has. That is only right when the
            copy&apos;s tables really are the same ones, which is what the check below
            looks at.
          </Typography>

          <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
            <TextField select fullWidth label="Copy" value={app} disabled={busy || planning}
              onChange={(e) => {
                const next = copies.find((c) => c.app === e.target.value);
                setApp(e.target.value);
                setVersion(next?.defaultVersion || "");
                forget();
              }}>
              {(copies || []).map((c) => (
                <MenuItem key={c.app} value={c.app}>{c.name} ({c.app})</MenuItem>
              ))}
            </TextField>
            <TextField select label="Release" value={version} disabled={busy || planning}
              sx={{ minWidth: 160 }}
              onChange={(e) => { setVersion(e.target.value); forget(); }}>
              {(chosen?.versions || []).map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
            </TextField>
          </Stack>
          {chosen?.description ? (
            <Typography variant="body2" color="text.secondary">{chosen.description}</Typography>
          ) : null}

          <Box>
            <Button variant="outlined" onClick={check} disabled={!app || !version || busy || planning}>
              {planning ? "Checking…" : plan ? "Check again" : "Check the change"}
            </Button>
            {planning ? <CircularProgress size={16} sx={{ ml: 1.5, verticalAlign: "middle" }} /> : null}
          </Box>

          {plan ? (
            <Stack spacing={2}>
              {plan.can_adopt ? (
                <Alert severity="success" variant="outlined">
                  <AlertTitle>Nothing stops it</AlertTitle>
                  The copy declares the tables and secrets {name} already has, with the
                  same keys. {plan.warns?.length ? "Read what to check first, below." : ""}
                </Alert>
              ) : (
                <Alert severity="error" variant="outlined">
                  <AlertTitle>It cannot go ahead</AlertTitle>
                  {plan.blocks.length === 1 ? "One thing stops it." : `${plan.blocks.length} things stop it.`}{" "}
                  Nothing has been changed.
                </Alert>
              )}
              <Problems title="What stops it" severity="error" rows={plan.blocks} />
              <Problems title="Check this first" severity="warning" rows={plan.warns} />
              <Parts label="Its tables" rows={plan.data?.tables} />
              <Parts label="Its secrets" rows={plan.data?.secrets} />
              <Ceiling ceiling={plan.ceiling} />
              {plan.info?.length ? (
                <Stack component="ul" spacing={0.25} sx={{ m: 0, pl: 2 }}>
                  {plan.info.map((i) => (
                    <li key={`${i.code}:${i.subject}`}><Typography variant="body2" color="text.secondary">{i.detail}</Typography></li>
                  ))}
                </Stack>
              ) : null}

              {plan.can_adopt ? (
                <Stack spacing={1}>
                  <Alert severity="warning" variant="outlined">
                    <AlertTitle>What this does not check</AlertTitle>
                    {plan.does_not_prove}
                  </Alert>
                  <FormControlLabel
                    control={<Checkbox checked={ack} onChange={(e) => setAck(e.target.checked)} disabled={busy} />}
                    label={
                      `I understand this changes what code opens ${name}'s existing tables, and that ` +
                      "point-in-time recovery is how to return them to this moment."
                    }
                  />
                </Stack>
              ) : null}
            </Stack>
          ) : null}

          {failure && !plan?.blocks?.length ? (
            <Alert severity="error" variant="outlined">{failure}</Alert>
          ) : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={!ready}>
          {busy ? "Replacing…" : "Replace its code"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

/** Put an adopted instance back on its own code, and say what that is not. */
export function RevertDialog({ open, instance, busy, onClose, onRevert }) {
  const [failure, setFailure] = useState(null);
  useEffect(() => { if (open) setFailure(null); }, [open]);
  const name = instance?.displayName || instance?.name || "";
  const from = instance?.adoption?.from;

  const submit = async () => {
    setFailure(null);
    try {
      await onRevert(instance.name);
    } catch (e) {
      setFailure(words(e));
    }
  };

  return (
    <Dialog open={open} onClose={busy ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Put {name} back on {instance?.app}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <Typography variant="body2">
            {name} goes back to running {instance?.app}
            {from?.version ? ` ${from.version}` : ""}, the release it was on. It is rebuilt over a
            few minutes and keeps serving what it has until then.
          </Typography>
          <Alert severity="warning" variant="outlined">
            <AlertTitle>This does not undo what the copy wrote</AlertTitle>
            Records {instance?.definition} added or changed while it ran stay as they are, and
            {" "}{instance?.app} will read them. To return the tables to before the change, use
            point-in-time recovery to the restore point recorded when it was adopted.
          </Alert>
          {failure ? <Alert severity="error" variant="outlined">{failure}</Alert> : null}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={busy}>Cancel</Button>
        <Button variant="contained" onClick={submit} disabled={busy}>
          {busy ? "Putting it back…" : `Put ${instance?.app || "it"} back`}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
